import * as THREE from 'three'
import { SceneRefs } from './types'

/**
 * Obsługa kliknięć myszą
 */
export function handleMouseClick(
  event: MouseEvent,
  container: HTMLDivElement,
  refs: SceneRefs,
  elements3D: Map<string, THREE.Group>,
  onElementSelect: (id: string) => void
): void {
  if (!refs.raycaster || !refs.mouse || !refs.camera || !refs.scene) return

  const rect = container.getBoundingClientRect()
  refs.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  refs.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

  refs.raycaster.setFromCamera(refs.mouse, refs.camera)
  const intersects = refs.raycaster.intersectObjects(refs.scene.children, true)

  if (intersects.length > 0) {
    // Znajdź element ogrodzenia
    let fenceElementId: string | null = null

    for (let i = 0; i < intersects.length; i++) {
      const clickedObject = intersects[i].object

      // Ignoruj elementy pomocnicze
      if (
        clickedObject.type === 'GridHelper' ||
        clickedObject.type === 'AmbientLight' ||
        clickedObject.type === 'DirectionalLight'
      ) {
        continue
      }

      // Sprawdź obiekt
      if (clickedObject.userData.fenceElementId) {
        fenceElementId = clickedObject.userData.fenceElementId
        break
      }

      // Sprawdź rodzica
      if (clickedObject.parent && clickedObject.parent.userData.fenceElementId) {
        fenceElementId = clickedObject.parent.userData.fenceElementId
        break
      }

      // Szukaj w hierarchii (max 15 poziomów)
      let searchObject: THREE.Object3D | null = clickedObject.parent
      let depth = 0
      while (searchObject && !fenceElementId && depth < 15) {
        if (searchObject.userData.fenceElementId) {
          fenceElementId = searchObject.userData.fenceElementId
          break
        }
        searchObject = searchObject.parent
        depth++
      }

      if (fenceElementId) break
    }

    if (fenceElementId) {
      onElementSelect(fenceElementId)
    }
  }

  // Wymuś renderowanie
  if (refs.renderer && refs.scene && refs.camera) {
    refs.renderer.render(refs.scene, refs.camera)
  }
}

/**
 * Obsługa ruchu myszy (throttled)
 */
let lastMouseRender = 0
export function handleMouseMove(refs: SceneRefs): void {
  if (refs.renderer && refs.scene && refs.camera) {
    const now = Date.now()
    if (!lastMouseRender || now - lastMouseRender > 100) {
      refs.renderer.render(refs.scene, refs.camera)
      lastMouseRender = now
    }
  }
}

/**
 * Obsługa przewijania (throttled)
 */
let lastScrollRender = 0
export function handleScroll(refs: SceneRefs): void {
  if (refs.renderer && refs.scene && refs.camera) {
    const now = Date.now()
    if (!lastScrollRender || now - lastScrollRender > 100) {
      refs.renderer.render(refs.scene, refs.camera)
      lastScrollRender = now
    }
  }
}

/**
 * Obsługa klawiatury
 */
export function handleKeyDown(refs: SceneRefs): void {
  if (refs.renderer && refs.scene && refs.camera) {
    refs.renderer.render(refs.scene, refs.camera)
  }
}

/**
 * Obsługa dotyku - pełna obsługa mobile
 */
export function handleTouchStart(
  event: TouchEvent,
  container: HTMLDivElement,
  refs: SceneRefs,
  elements3D: Map<string, THREE.Group>,
  onElementSelect: (id: string) => void
): void {
  if (!refs.raycaster || !refs.mouse || !refs.camera || !refs.scene) return
  
  // Zapobiegaj domyślnym akcjom (scroll, zoom)
  event.preventDefault()
  
  // Użyj pierwszego dotyku
  const touch = event.touches[0]
  if (!touch) return
  
  const rect = container.getBoundingClientRect()
  refs.mouse.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1
  refs.mouse.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1

  refs.raycaster.setFromCamera(refs.mouse, refs.camera)
  const intersects = refs.raycaster.intersectObjects(refs.scene.children, true)

  if (intersects.length > 0) {
    // Znajdź element ogrodzenia
    let fenceElementId: string | null = null

    for (let i = 0; i < intersects.length; i++) {
      const clickedObject = intersects[i].object

      // Ignoruj elementy pomocnicze
      if (
        clickedObject.type === 'GridHelper' ||
        clickedObject.type === 'AmbientLight' ||
        clickedObject.type === 'DirectionalLight'
      ) {
        continue
      }

      // Sprawdź obiekt
      if (clickedObject.userData.fenceElementId) {
        fenceElementId = clickedObject.userData.fenceElementId
        break
      }

      // Sprawdź rodzica
      if (clickedObject.parent && clickedObject.parent.userData.fenceElementId) {
        fenceElementId = clickedObject.parent.userData.fenceElementId
        break
      }

      // Sprawdź dziadka (dla zagnieżdżonych grup)
      if (clickedObject.parent?.parent && clickedObject.parent.parent.userData.fenceElementId) {
        fenceElementId = clickedObject.parent.parent.userData.fenceElementId
        break
      }
    }

    if (fenceElementId) {
      console.log(`🎯 Touch: Wybrano element ${fenceElementId}`)
      onElementSelect(fenceElementId)
    } else {
      console.log('🎯 Touch: Kliknięto w pustą przestrzeń')
      onElementSelect('')
    }
  }
}

/**
 * Obsługa ruchu dotyku (dla przyszłych funkcji)
 */
export function handleTouchMove(
  event: TouchEvent,
  refs: SceneRefs
): void {
  // Zapobiegaj domyślnym akcjom
  event.preventDefault()
  
  // Tutaj można dodać obsługę przesuwania kamery lub elementów
  if (refs.renderer && refs.scene && refs.camera) {
    refs.renderer.render(refs.scene, refs.camera)
  }
}

/**
 * Obsługa zakończenia dotyku
 */
export function handleTouchEnd(
  event: TouchEvent,
  refs: SceneRefs
): void {
  // Zapobiegaj domyślnym akcjom
  event.preventDefault()
  
  if (refs.renderer && refs.scene && refs.camera) {
    refs.renderer.render(refs.scene, refs.camera)
  }
}

/**
 * Pętla animacji
 */
export function animate(refs: SceneRefs): void {
  requestAnimationFrame(() => animate(refs))

  if (refs.controls) {
    refs.controls.update()
  }

  if (refs.renderer && refs.scene && refs.camera) {
    refs.renderer.render(refs.scene, refs.camera)
  }
}










